/**
 * @license
 * Copyright Google Inc. All Rights Reserved.
 *
 * Use of this source code is governed by an MIT-style license that can be
 * found in the LICENSE file at https://angular.io/license
 */


import {Injector, THROW_IF_NOT_FOUND} from '../di/injector';
import {Type} from '../type';
import {stringify} from '../util';

import {ComponentFactory} from './component_factory';
import {CodegenComponentFactoryResolver, ComponentFactoryBoundToModule, ComponentFactoryResolver} from './component_factory_resolver';
/**
 * Represents an instance of an NgModule created via a {\@link NgModuleFactory}.
 * 
 * `NgModuleRef` provides access to the NgModule Instance as well other objects related to this
 * NgModule Instance.
 * 
 * \@stable
 * @abstract
 */
export abstract class NgModuleRef<T> {
/**
 * The injector that contains all of the providers of the NgModule.
 * @abstract
 * @return {?}
 */
injector() {}
/**
 * The ComponentFactoryResolver to get hold of the ComponentFactories
 * declared in the `entryComponents` property of the module.
 * @abstract
 * @return {?}
 */
componentFactoryResolver() {}
/**
 * The NgModule instance.
 * @abstract
 * @return {?}
 */
instance() {}
/**
 * Destroys the module instance and all of the data structures associated with it.
 * @abstract
 * @return {?}
 */
destroy() {}
/**
 * Allows to register a callback that will be called when the module is destroyed.
 * @abstract
 * @param {?} callback
 * @return {?}
 */
onDestroy(callback: () => void) {}
}
/**
 * \@experimental
 */
export class NgModuleFactory<T> {
/**
 * @param {?} _injectorClass
 * @param {?} _moduleType
 */
constructor(
private _injectorClass: {new (parentInjector: Injector): NgModuleInjector<T>},
private _moduleType: Type<T>) {}
/**
 * @return {?}
 */
get moduleType(): Type<T> { return this._moduleType; }
/**
 * @param {?} parentInjector
 * @return {?}
 */
create(parentInjector: Injector|null): NgModuleRef<T> {
    const /** @type {?} */ instance = new this._injectorClass(parentInjector || Injector.NULL);
    instance.create();
    return instance;
  }
}

function NgModuleFactory_tsickle_Closure_declarations() {
/** @type {?} */
NgModuleFactory.prototype._injectorClass;
/** @type {?} */
NgModuleFactory.prototype._moduleType;
}


const /** @type {?} */ _UNDEFINED = new Object();
/**
 * @abstract
 */
export abstract class NgModuleInjector<T> implements Injector, NgModuleRef<T> {
  bootstrapFactories: ComponentFactory<any>[];
  instance: T;
private _destroyListeners: (() => void)[] = [];
private _destroyed: boolean = false;
private _cmpFactoryResolver: CodegenComponentFactoryResolver;
/**
 * @param {?} parent
 * @param {?} factories
 * @param {?} bootstrapFactories
 */
constructor(
public parent: Injector, factories: ComponentFactory<any>[],
      bootstrapFactories: ComponentFactory<any>[]) {
    this.bootstrapFactories =
        bootstrapFactories.map(f => new ComponentFactoryBoundToModule(f, this));
    this._cmpFactoryResolver = new CodegenComponentFactoryResolver(
        factories, parent.get(ComponentFactoryResolver, ComponentFactoryResolver.NULL), this);
  }
/**
 * @return {?}
 */
create() { this.instance = this.createInternal(); }
/**
 * @abstract
 * @return {?}
 */
createInternal() {}
/**
 * @param {?} token
 * @param {?=} notFoundValue
 * @return {?}
 */
get(token: any, notFoundValue: any = THROW_IF_NOT_FOUND): any {
    if (token === Injector || token === NgModuleRef) {
      return this;
    }

    if (token === ComponentFactoryResolver) {
      return this._cmpFactoryResolver;
    }

    const /** @type {?} */ result = this.getInternal(token, _UNDEFINED);
    return result === _UNDEFINED ? this.parent.get(token, notFoundValue) : result;
  }
/**
 * @abstract
 * @param {?} token
 * @param {?} notFoundValue
 * @return {?}
 */
getInternal(token: any, notFoundValue: any) {}
/**
 * @return {?}
 */
get injector(): Injector { return this; }
/**
 * @return {?}
 */
get componentFactoryResolver(): ComponentFactoryResolver { return this._cmpFactoryResolver; }
/**
 * @return {?}
 */
destroy(): void {
    if (this._destroyed) {
      throw new Error(
          `The ng module ${stringify(this.instance.constructor)} has already been destroyed.`);
    }
    this._destroyed = true;
    this.destroyInternal();
    this._destroyListeners.forEach((listener) => listener());
  }
/**
 * @param {?} callback
 * @return {?}
 */
onDestroy(callback: () => void): void { this._destroyListeners.push(callback); }
/**
 * @abstract
 * @return {?}
 */
destroyInternal() {}
}

function NgModuleInjector_tsickle_Closure_declarations() {
/** @type {?} */
NgModuleInjector.prototype.bootstrapFactories;
/** @type {?} */
NgModuleInjector.prototype.instance;
/** @type {?} */
NgModuleInjector.prototype._destroyListeners;
/** @type {?} */
NgModuleInjector.prototype._destroyed;
/** @type {?} */
NgModuleInjector.prototype._cmpFactoryResolver;
/** @type {?} */
NgModuleInjector.prototype.parent;
}

